import React, { useEffect, useState } from 'react';
import api, { formatApiError } from '../api';
import PageHeader from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Plus, Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';

export default function ModulesPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get('/modules');
            setItems(r.data.items);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const toggleActive = async (m) => {
        try {
            await api.patch(`/modules/${m.id}`, { active: !m.active });
            load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    const grouped = items.reduce((acc, m) => {
        const cat = m.category || 'Other';
        (acc[cat] = acc[cat] || []).push(m);
        return acc;
    }, {});

    return (
        <div data-testid="modules-page" className="bm-fade-in">
            <PageHeader
                title="Modules"
                description="The module library — reusable building blocks included in your proposals and contracts."
                actions={
                    <Button onClick={() => setCreating(true)} className="h-9" style={{ background: 'var(--bm-primary)' }} data-testid="modules-new-btn">
                        <Plus size={16} className="mr-1" /> New module
                    </Button>
                }
            />

            <div className="px-8 py-6 space-y-7">
                {loading && <div className="text-[color:var(--bm-text-muted)]">Loading…</div>}
                {!loading && Object.keys(grouped).sort().map(cat => (
                    <div key={cat}>
                        <div className="text-[11px] uppercase tracking-[0.12em] text-[color:var(--bm-text-subtle)] font-semibold mb-3">{cat}</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {grouped[cat].map(m => (
                                <div key={m.id} className={`bg-white rounded-xl border p-5 transition ${m.active ? 'border-[color:var(--bm-border)]' : 'border-dashed border-[color:var(--bm-border)] opacity-60'}`} data-testid={`module-card-${m.id}`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="font-display text-[15px] font-semibold text-[color:var(--bm-text)]">{m.name}</div>
                                        <div className="flex items-center gap-1">
                                            <button onClick={() => setEditing(m)} className="p-1.5 text-[color:var(--bm-text-subtle)] hover:text-[color:var(--bm-primary)]" title="Edit"><Pencil size={14} /></button>
                                            <button onClick={() => toggleActive(m)} className="p-1.5 text-[color:var(--bm-text-subtle)] hover:text-[color:var(--bm-primary)]" title={m.active ? 'Deactivate' : 'Activate'}>
                                                {m.active ? <Check size={14} className="text-[color:var(--bm-accent-600)]" /> : <X size={14} />}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="text-[12.5px] text-[color:var(--bm-text-muted)] mt-1.5 line-clamp-2 min-h-[34px]">{m.description}</div>
                                    <div className="flex items-center gap-4 mt-4 pt-3 border-t border-[color:var(--bm-border)] text-[12px]">
                                        <div>
                                            <span className="text-[color:var(--bm-text-subtle)]">Monthly</span>
                                            <span className="ml-1 font-semibold text-[color:var(--bm-text)]">£{m.suggested_monthly_price}</span>
                                        </div>
                                        <div>
                                            <span className="text-[color:var(--bm-text-subtle)]">Setup</span>
                                            <span className="ml-1 font-semibold text-[color:var(--bm-text)]">£{m.suggested_setup_price}</span>
                                        </div>
                                        <div className="ml-auto flex items-center gap-0.5">
                                            {[1, 2, 3, 4, 5].map(i => (
                                                <span key={i} className={`w-1.5 h-3 rounded-sm ${i <= (m.effort_rating || 0) ? 'bg-[color:var(--bm-accent)]' : 'bg-[color:var(--bm-border)]'}`} />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <ModuleDialog
                open={creating}
                onClose={() => setCreating(false)}
                onSaved={() => { setCreating(false); load(); }}
            />
            <ModuleDialog
                open={!!editing}
                module={editing}
                onClose={() => setEditing(null)}
                onSaved={() => { setEditing(null); load(); }}
            />
        </div>
    );
}

function ModuleDialog({ open, onClose, module: mod, onSaved }) {
    const empty = { name: '', description: '', category: '', effort_rating: 3, suggested_monthly_price: 0, suggested_setup_price: 0, active: true };
    const [form, setForm] = useState(empty);
    useEffect(() => { setForm(mod ? { ...empty, ...mod } : empty); }, [mod, open]);  // eslint-disable-line

    const submit = async (e) => {
        e.preventDefault();
        try {
            if (mod) await api.patch(`/modules/${mod.id}`, form);
            else await api.post('/modules', form);
            toast.success(mod ? 'Module updated' : 'Module created');
            onSaved();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-[520px]">
                <DialogHeader>
                    <DialogTitle>{mod ? 'Edit module' : 'New module'}</DialogTitle>
                    <DialogDescription className="text-[12.5px] text-[color:var(--bm-text-muted)]">Modules are reusable building blocks attached to proposals and contracts.</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3 mt-2">
                    <div>
                        <Label className="text-[12.5px]">Name *</Label>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5 h-10" />
                    </div>
                    <div>
                        <Label className="text-[12.5px]">Description</Label>
                        <Textarea value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" rows={2} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[12.5px]">Category</Label>
                            <Input value={form.category || ''} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1.5 h-10" />
                        </div>
                        <div>
                            <Label className="text-[12.5px]">Effort (1-5)</Label>
                            <Input type="number" min={1} max={5} value={form.effort_rating} onChange={(e) => setForm({ ...form, effort_rating: parseInt(e.target.value) || 3 })} className="mt-1.5 h-10" />
                        </div>
                        <div>
                            <Label className="text-[12.5px]">Monthly price (£)</Label>
                            <Input type="number" step="0.01" value={form.suggested_monthly_price} onChange={(e) => setForm({ ...form, suggested_monthly_price: parseFloat(e.target.value) || 0 })} className="mt-1.5 h-10" />
                        </div>
                        <div>
                            <Label className="text-[12.5px]">Setup price (£)</Label>
                            <Input type="number" step="0.01" value={form.suggested_setup_price} onChange={(e) => setForm({ ...form, suggested_setup_price: parseFloat(e.target.value) || 0 })} className="mt-1.5 h-10" />
                        </div>
                    </div>
                    <DialogFooter className="pt-2">
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" style={{ background: 'var(--bm-primary)' }}>{mod ? 'Save' : 'Create'}</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
